import { AxiosError } from 'axios';

/**
 * Extract a user-friendly error message from an unknown error.
 * Handles Axios errors, standard Error objects, and unknown types.
 *
 * @param err - The caught error (typed as unknown)
 * @param fallback - Default message if no better message can be extracted
 * @returns A string error message suitable for display to the user
 */
/** First readable message out of the API's `details.fields` map.
 *
 * Shape: `{ details: { fields: { field_name: ['message', ...] } } }`. Several
 * fields can fail at once; they are joined so nothing is hidden behind the
 * first one.
 */
function getFieldErrorMessage(data: Record<string, unknown> | undefined): string | undefined {
  const details = data?.['details'] as Record<string, unknown> | undefined;
  const fields = details?.['fields'] as Record<string, unknown> | undefined;
  if (!fields || typeof fields !== 'object') return undefined;
  // De-duplicated: two fields refused for the same reason (one colour picker
  // writing two colour fields) read as one sentence, not the same one twice.
  const messages = Array.from(new Set(Object.values(fields)
    .flatMap(v => (Array.isArray(v) ? v : [v]))
    .filter((v): v is string => typeof v === 'string' && v.length > 0)));
  return messages.length ? messages.join(' ') : undefined;
}

const NAMED_ENTITIES: Record<string, string> = { quot: '"', amp: '&', lt: '<', gt: '>', apos: "'", nbsp: ' ' };

/**
 * Undo the API's HTML-escaping of error text.
 *
 * The backend's exception handler escapes `< > & " '` in every message
 * (CWE-79 hardening for HTML-rendering consumers), so a band label quoted
 * as "D" reached the toast as &quot;D&quot;. This app renders messages as
 * React text nodes, which never interpret HTML, so decoding is safe here
 * and the escaping stays in place for everyone else.
 */
export function decodeApiText(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, body: string) => {
    const lower = body.toLowerCase();
    if (lower.startsWith('#x')) return String.fromCodePoint(parseInt(lower.slice(2), 16));
    if (lower.startsWith('#')) return String.fromCodePoint(parseInt(lower.slice(1), 10));
    return NAMED_ENTITIES[lower] ?? whole;
  });
}

/**
 * Per-field messages from a refused write, for showing under each input.
 *
 * Reads the standard envelope's `details.fields` map and keeps the first
 * message for each field. Anything that is not a field error — a 403, a
 * refused transition, a network failure — gives an empty object, so the
 * caller can fall back to its usual page-level handling.
 *
 * @param err - The caught error (typed as unknown)
 * @returns Field name to readable message; empty when there are none
 * @example
 *   const fields = getApiFieldErrors(err);
 *   if (fields['quantity_approved']) setErrors(fields);
 */
export function getApiFieldErrors(err: unknown): Record<string, string> {
  const response = (err as { response?: { data?: unknown } } | null)?.response;
  const data = response?.data as Record<string, unknown> | undefined;
  const details = data?.['details'] as Record<string, unknown> | undefined;
  const fields = details?.['fields'];
  const result: Record<string, string> = {};
  if (!fields || typeof fields !== 'object') return result;
  for (const [name, value] of Object.entries(fields as Record<string, unknown>)) {
    const first = Array.isArray(value) ? value[0] : value;
    if (typeof first === 'string' && first.length > 0) {
      result[name] = decodeApiText(first);
    }
  }
  return result;
}

export function getApiErrorMessage(err: unknown, fallback = 'An unexpected error occurred'): string {
  return decodeApiText(extractApiErrorMessage(err, fallback));
}

/**
 * The machine-readable `code` from the API's error envelope, if any.
 *
 * Lets a screen tell one refusal from another (e.g. `manages_own_users`)
 * without parsing message text.
 *
 * @param err - The caught error (typed as unknown)
 * @returns The `code` string, or undefined for non-API errors
 */
export function getApiErrorCode(err: unknown): string | undefined {
  if (!(err instanceof AxiosError)) return undefined;
  const data = err.response?.data as Record<string, unknown> | undefined;
  const code = data?.['code'];
  return typeof code === 'string' ? code : undefined;
}

function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    return (data?.['detail'] as string)
      || (data?.['error'] as string)
      // A DRF field error arrives inside the standard envelope as
      // details.fields — and the sibling `message` is the useless generic
      // "Validation failed". Read the field message first, or every rejected
      // form tells the user only that something was wrong, never what.
      || getFieldErrorMessage(data)
      || (data?.['message'] as string)
      || err.message
      || fallback;
  }
  if (err instanceof Error) {
    return err.message || fallback;
  }
  if (typeof err === 'string') {
    return err;
  }
  return fallback;
}
